import { useState, useEffect, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { 
  useAuthStore, 
  MASTER_LOGIN_URL,
  getAuthCookie,
  getOrgCookie,
} from '@quotech/auth'
import { jobQaHubPath, openJobQaInHub, qaRecordPath } from './records-list/openJobQaInHub'
import api from '@/lib/api'
import { JobQaHomeRow, openBlankQa } from './records/JobQaHomeRow'
import { JobQaHomeTable } from './records/JobQaHomeTable'
import { useJobRows } from './records/useJobRows'
import { useQaAppCapabilities } from '@/lib/useQaAppCapabilities'
import { jobFromV2Row } from './new-inspection/jobFromV2Row'
import { cn, useIsMobile } from '@/lib/utils'
import type { InspectionStatus } from '@/types/qa'
import { RecordsFilterBar } from './records-list/RecordsFilterBar'
import { inspectionListParams, type InspectionDateMode } from './records-list/inspectionListFilters'
import { useQaJobStatusSelection } from './records-list/useQaJobStatusSelection'
import { JobSearchResults } from './records-list/JobSearchResults'
import {
  Plus,
  Sparkles,
  FileText,
  AlertTriangle,
  Loader2,
  LogIn,
} from 'lucide-react'

const statusOptions: { value: InspectionStatus | '' | 'none'; label: string }[] = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'ready_for_pack', label: 'Ready for Pack' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'archived', label: 'Archived' },
  { value: 'none', label: 'No QA yet' },
]

export default function RecordsPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<InspectionStatus | '' | 'none'>('')
  const [jobFilter, setJobFilter] = useState('')
  const [clientFilter, setClientFilter] = useState('')
  const [siteFilter, setSiteFilter] = useState('')
  const [dateMode, setDateMode] = useState<InspectionDateMode>('any')
  const [withinDays, setWithinDays] = useState(30)
  const [dateValue, setDateValue] = useState('')
  const [page, setPage] = useState(1)
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')
  const [pendingJobId, setPendingJobId] = useState<string | null>(null)
  const [openError, setOpenError] = useState<string | null>(null)
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { canCreateQaJob } = useQaAppCapabilities()
  const queryClient = useQueryClient()
  const aiModeQuery = useQuery({
    queryKey: ['qa-ai-mode'],
    queryFn: async () => {
      const response = await api.get('/qa/ai-mode/')
      return Boolean(response.data?.enabled)
    },
  })
  const aiMode = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await api.patch('/qa/ai-mode/', { enabled })
      return Boolean(response.data?.enabled)
    },
    onSuccess: (enabled) => queryClient.setQueryData(['qa-ai-mode'], enabled),
  })

  // Auth state - use Zustand store (synced with cookies via SessionManager)
  const storeOrgId = useAuthStore((s) => s.organisationId)
  const storeToken = useAuthStore((s) => s.accessToken)
  const checkAuth = useAuthStore((s) => s.checkAuth)
  
  // CRITICAL FIX: Read cookies synchronously via useMemo (not useEffect)
  // This ensures isAuthReady is correct on FIRST render, enabling queries immediately.
  // The pattern matches offline.ts which works correctly.
  const { accessToken, organisationId } = useMemo(() => {
    const cookieToken = getAuthCookie()
    const cookieOrg = getOrgCookie()
    return {
      accessToken: cookieToken || storeToken,
      organisationId: cookieOrg?.id || storeOrgId,
    }
  }, [storeOrgId, storeToken])
  
  // Local state for auth checking (only used when org is missing)
  const [authCheckAttempted, setAuthCheckAttempted] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(false)
  
  // Try to fetch org context if we have token but no org
  useEffect(() => {
    const tryFetchOrg = async () => {
      if (accessToken && !organisationId && !authCheckAttempted && !isCheckingAuth) {
        setIsCheckingAuth(true)
        try {
          await checkAuth()
        } catch (e) {
          if (import.meta.env.DEV) console.error('[QA Records] checkAuth failed:', e)
        } finally {
          setIsCheckingAuth(false)
          setAuthCheckAttempted(true)
        }
      }
    }
    tryFetchOrg()
  }, [accessToken, organisationId, authCheckAttempted, isCheckingAuth, checkAuth])
  
  const isAuthReady = !!organisationId
  const hasNoOrg = authCheckAttempted && !organisationId && !!accessToken
  const jobStatus = useQaJobStatusSelection(organisationId, isAuthReady, setPage)

  const trimmedJobFilter = jobFilter.trim()

  const listParams = inspectionListParams({
    status: statusFilter,
    search: searchQuery,
    client: clientFilter,
    site: siteFilter,
    job: trimmedJobFilter,
    jobStatusBuckets: jobStatus.jobStatusBuckets,
    dateMode,
    withinDays,
    dateValue,
    page,
  })
  const { data, isLoading, error, refetch } = useJobRows({
    enabled: isAuthReady,
    params: listParams,
  })

  const jobRows = data?.results ?? []
  const jobSearchText = (trimmedJobFilter || searchQuery).trim()
  const [debouncedJobSearch, setDebouncedJobSearch] = useState(jobSearchText)
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedJobSearch(jobSearchText), 300)
    return () => window.clearTimeout(timer)
  }, [jobSearchText])
  // Reused from: packages/job-hub/src/jobs-list/gateway/platformJobsListGateway.ts (searchSuggestions: same endpoint and params)
  const jobSearch = useQuery({
    queryKey: ['qa-record-job-search', debouncedJobSearch, organisationId],
    queryFn: async () => {
      const { data: payload } = await api().get('/v2/jobs/', {
        params: { search: debouncedJobSearch, page_size: '20', tenant: organisationId },
      })
      const rows = Array.isArray(payload?.results) ? payload.results : Array.isArray(payload) ? payload : []
      return rows.map((row: Record<string, unknown>) => jobFromV2Row(row))
    },
    enabled: isAuthReady && debouncedJobSearch.length > 0,
  })
  const jobHits = jobSearch.data ?? []

  async function onOpenJobQa(jobNumber: string) {
    if (pendingJobId) return
    setPendingJobId(jobNumber)
    setOpenError(null)
    try {
      const inspectionId = await openJobQaInHub(jobNumber)
      navigate(inspectionId ? qaRecordPath(inspectionId) : jobQaHubPath(jobNumber))
    } catch (error) {
      setOpenError(error instanceof Error ? error.message : `Could not open QA for job ${jobNumber}.`)
    } finally {
      setPendingJobId(null)
    }
  }

  const showLoading = isLoading || (!isAuthReady && !authCheckAttempted)
  
  // If we have token but still no org after check, show re-login prompt
  if (hasNoOrg) {
    const returnUrl = encodeURIComponent(window.location.href)
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/30 mx-auto mb-4 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Organisation Not Found</h2>
          <p className="text-muted-foreground mb-6">
            Your session doesn't have an organisation context. This usually happens when cookies expire. Please log in again.
          </p>
          <a
            href={`${MASTER_LOGIN_URL}?returnTo=${returnUrl}&reason=no_org`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors"
          >
            <LogIn className="w-5 h-5" />
            Log In Again
          </a>
        </div>
      </div>
    )
  }
  
  // Show loading while checking auth
  if (isCheckingAuth) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Loading organisation...</p>
        </div>
      </div>
    )
  }

  async function onOpenEmptyRow(row: (typeof jobRows)[number]) {
    if (pendingJobId) return
    setPendingJobId(String(row.job_id))
    setOpenError(null)
    try {
      await openBlankQa(row)
    } catch (err) {
      setOpenError(err instanceof Error ? err.message : `Could not open QA for job ${row.job_id}.`)
      setPendingJobId(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">QA Inspections</h1>
          <p className="text-muted-foreground">Manage quality assurance inspections and checklists</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-pressed={aiModeQuery.data === true}
            onClick={() => aiMode.mutate(aiModeQuery.data !== true)}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 font-medium text-foreground"
          >
            <Sparkles className="h-4 w-4" aria-hidden />
            AI mode
          </button>
        </div>
      </div>

      {/* Filters */}
      <RecordsFilterBar
        searchQuery={searchQuery}
        onSearchChange={(value) => {
          setSearchQuery(value)
          setPage(1)
        }}
        jobFilter={jobFilter}
        onJobChange={(value) => {
          setJobFilter(value)
          setPage(1)
        }}
        clientFilter={clientFilter}
        onClientChange={(value) => {
          setClientFilter(value)
          setPage(1)
        }}
        siteFilter={siteFilter}
        onSiteChange={(value) => {
          setSiteFilter(value)
          setPage(1)
        }}
        dateMode={dateMode}
        onDateModeChange={(value) => {
          setDateMode(value)
          setPage(1)
        }}
        withinDays={withinDays}
        onWithinDaysChange={(value) => {
          setWithinDays(value)
          setPage(1)
        }}
        dateValue={dateValue}
        onDateValueChange={(value) => {
          setDateValue(value)
          setPage(1)
        }}
        statusFilter={statusFilter}
        onStatusChange={(value) => {
          setStatusFilter(value)
          setPage(1)
        }}
        statusOptions={statusOptions}
        jobStatusBuckets={jobStatus.jobStatusBuckets}
        onToggleJobStatus={jobStatus.onToggleJobStatus}
        jobStatusAuthority={jobStatus.authority}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onRefresh={() => refetch()}
      />

      <JobSearchResults
        text={jobRows.some((row) => String(row.job_id) === debouncedJobSearch.replace(/^qt-?/i, '')) ? '' : debouncedJobSearch}
        loading={jobSearch.isFetching}
        failed={jobSearch.isError}
        jobs={jobHits}
        pendingJobId={pendingJobId}
        onOpen={onOpenJobQa}
      />

      {/* Inspections list/grid */}
      <div>
        {showLoading ? (
          <div className={cn(
            viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' 
              : 'p-4 space-y-3'
          )}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div 
                key={i} 
                className={cn(
                  "bg-muted rounded-xl animate-pulse",
                  viewMode === 'grid' ? 'h-32' : 'h-16'
                )}
              />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-destructive/10 mx-auto mb-4 flex items-center justify-center">
              <FileText className="w-8 h-8 text-destructive" />
            </div>
            <p className="font-medium text-foreground">Error loading inspections</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              {(error as Error)?.message || 'Please try again later'}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : jobRows.length ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {jobRows.map((row) => (
                <JobQaHomeRow
                  key={String(row.job_id)}
                  row={row}
                  mobile={isMobile}
                  pending={pendingJobId === String(row.job_id)}
                  onOpenEmpty={(next) => { void onOpenEmptyRow(next) }}
                />
              ))}
            </div>
          ) : (
            <JobQaHomeTable
              rows={jobRows}
              pendingJobId={pendingJobId}
              onOpenEmpty={(next) => { void onOpenEmptyRow(next) }}
              onAdd={canCreateQaJob ? () => navigate('/records/new') : undefined}
            />
          )
        ) : (
          <div className="text-center py-16 bg-card rounded-xl border border-border">
            <div className="w-16 h-16 rounded-2xl bg-muted mx-auto mb-4 flex items-center justify-center">
              <FileText className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="font-medium text-foreground">No inspections found</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
              {searchQuery || statusFilter || trimmedJobFilter || clientFilter || siteFilter || dateMode !== 'any' || jobStatus.jobStatusActive
                ? 'Try adjusting your search or filters'
                : 'Create your first QA inspection to get started'}
            </p>
            {/* A job with no inspection yet returns zero rows here, which used
                to strand the user (operator had to hand-edit the URL on a
                phone, 2026-09-15). A numeric search is a job number — offer
                the direct door into that job's QA tab. */}
            {(() => {
              const numeric = (trimmedJobFilter || searchQuery.trim()).replace(/^qt-?/i, '')
              if (!/^\d+$/.test(numeric)) return null
              return (
                <button
                  type="button"
                  disabled={pendingJobId !== null}
                  onClick={() => onOpenJobQa(numeric)}
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg border border-input text-sm font-medium text-foreground hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {pendingJobId === numeric && <Loader2 className="w-4 h-4 animate-spin" />}
                  Open job {numeric} QA
                </button>
              )
            })()}
            {openError && (
              <p className="text-sm text-destructive mt-2">{openError}</p>
            )}
            {canCreateQaJob && !searchQuery && !statusFilter && !trimmedJobFilter && !clientFilter && !siteFilter && dateMode === 'any' && !jobStatus.jobStatusActive && (
              <Link
                to="/records/new"
                className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                New Inspection
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && data.count > 20 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-card rounded-xl border border-border p-4">
          <p className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">{((page - 1) * 20) + 1}</span> - <span className="font-medium text-foreground">{Math.min(page * 20, data.count)}</span> of <span className="font-medium text-foreground">{data.count}</span> inspections
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-lg border border-input text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page * 20 >= data.count}
              className="px-4 py-2 rounded-lg border border-input text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
